import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { useUnitSummary, useUnitExamTimeline } from '../hooks/usePYQAnalytics'
import { usePYQAnalyticsStore } from '../stores/pyqAnalyticsStore'
import { ExamTimelineChart } from '../components/PastExamsDashboard/charts'
import { PriorityBadge, TrendBadge } from '../components/PastExamsDashboard/shared/badges'
import { DIFFICULTY_COLORS, SUBJECT_COLORS } from '../constants/colors'
import type { DifficultyLevel } from '../types'
import { fbTrackViewContent } from '@/shared/utils/facebookPixel'

// Helper to get subject color from various code formats
function getSubjectColor(subjectCode?: string): string {
  const code = (subjectCode || '').toUpperCase()
  if (code.includes('PHY')) return SUBJECT_COLORS.NEET_UG_PHY
  if (code.includes('CHEM')) return SUBJECT_COLORS.NEET_UG_CHEM
  if (code.includes('BIO')) return SUBJECT_COLORS.NEET_UG_BIO
  return '#6B7280'
}

// Helper to get subject name from code
function getSubjectName(subjectCode?: string): string {
  const code = (subjectCode || '').toUpperCase()
  if (code.includes('PHY')) return 'Physics'
  if (code.includes('CHEM')) return 'Chemistry'
  if (code.includes('BIO')) return 'Biology'
  return subjectCode || 'Unknown'
}

export default function UnitDetailPage() {
  const { unitCode } = useParams<{ unitCode: string }>()
  const navigate = useNavigate()
  const { yearRangeStart, yearRangeEnd } = usePYQAnalyticsStore()

  // Track ViewContent for FB Pixel
  useEffect(() => {
    if (unitCode) {
      fbTrackViewContent('unit_detail', unitCode, `Unit: ${unitCode}`)
    }
  }, [unitCode])

  const { data: summary, isLoading: isSummaryLoading } = useUnitSummary(unitCode || null, {
    yearRangeStart,
    yearRangeEnd,
  })

  const { data: timeline, isLoading: isTimelineLoading } = useUnitExamTimeline(unitCode || null, {
    yearRangeStart,
    yearRangeEnd,
  })

  const isLoading = isSummaryLoading || isTimelineLoading

  const handleTopicClick = (topicCode: string) => {
    navigate(`/analytics/past-exams/topic/${topicCode}`)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
                {summary?.unit?.unitName || 'Loading...'}
              </h1>
              {summary?.unit && (
                <div className="flex items-center gap-2 mt-1">
                  <span
                    className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full"
                    style={{
                      backgroundColor: getSubjectColor(summary.unit.subjectCode) + '20',
                      color: getSubjectColor(summary.unit.subjectCode),
                    }}
                  >
                    {getSubjectName(summary.unit.subjectCode)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
        ) : summary?.unit ? (
          <div className="space-y-6">
            {/* Key Stats - Row 1 */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-sm font-medium text-gray-700 mb-4">Overview</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-blue-600">{summary.unit.totalQuestions ?? 0}</p>
                  <p className="text-xs sm:text-sm text-gray-600">Total Questions</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-green-600">
                    {(summary.unit.avgQuestionsPerExam ?? 0).toFixed(1)}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600">Avg/Exam</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-purple-600">{summary.unit.totalTopics ?? 0}</p>
                  <p className="text-xs sm:text-sm text-gray-600">Topics</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-orange-600">
                    {(summary.unit.importanceScore ?? 0).toFixed(1)}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600">Importance</p>
                </div>
              </div>
            </div>

            {/* Key Stats - Row 2 */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-sm font-medium text-gray-700 mb-4">Metrics</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-gray-700">
                    {(summary.unit.absoluteWeightage ?? 0).toFixed(1)}%
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600">Weightage</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-gray-700">
                    {(summary.unit.difficultyIndex ?? 0).toFixed(1)}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600">Difficulty Index</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-gray-700">
                    {(summary.unit.conceptualDensity ?? 0).toFixed(1)}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600">Conceptual Density</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-gray-700">
                    {((summary.unit.topicCoverageRatio ?? 0) * 100).toFixed(0)}%
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600">Topic Coverage</p>
                </div>
              </div>
            </div>

            {/* Difficulty Distribution */}
            {summary.difficultyDistribution && summary.difficultyDistribution.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h2 className="text-sm font-medium text-gray-700 mb-4">Difficulty Distribution</h2>
                <div className="flex gap-3">
                  {summary.difficultyDistribution.map((diff) => (
                    <div
                      key={diff.name}
                      className="flex-1 rounded-lg p-4 text-center"
                      style={{
                        backgroundColor: (DIFFICULTY_COLORS[diff.name as DifficultyLevel] || '#6B7280') + '20',
                      }}
                    >
                      <p
                        className="text-2xl font-bold"
                        style={{ color: DIFFICULTY_COLORS[diff.name as DifficultyLevel] || '#6B7280' }}
                      >
                        {diff.value ?? 0}
                      </p>
                      <p className="text-xs text-gray-600">{diff.name || 'Unknown'}</p>
                      <p className="text-xs text-gray-500">{(diff.percentage ?? 0).toFixed(0)}%</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Exam Timeline */}
            {timeline && timeline.timeline && timeline.timeline.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h2 className="text-sm font-medium text-gray-700 mb-4">Questions Over Time</h2>
                <ExamTimelineChart
                  data={timeline.timeline.map((t) => ({
                    year: t.year,
                    questionCount: t.questionCount,
                    examName: t.examName,
                  }))}
                />
              </div>
            )}

            {/* Topic Breakdown */}
            {summary.topics && summary.topics.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h2 className="text-sm font-medium text-gray-700 mb-4">Topics in this Unit</h2>
                <div className="space-y-2">
                  {summary.topics.map((topic) => (
                    <div
                      key={topic.topicCode}
                      onClick={() => handleTopicClick(topic.topicCode)}
                      className="flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-all"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900">{topic.topicName || 'Unknown Topic'}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <span className="text-sm text-gray-500">
                            {topic.totalQuestions ?? 0} questions
                          </span>
                          {topic.priorityLevel && <PriorityBadge priority={topic.priorityLevel} />}
                          {topic.trendDirection && <TrendBadge trend={topic.trendDirection} />}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <span className="text-sm font-medium text-gray-600">
                          {(topic.importanceScore ?? 0).toFixed(1)}
                        </span>
                        <ArrowLeft className="w-4 h-4 text-gray-400 rotate-180" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-20 text-gray-500">
            Unit not found
          </div>
        )}
      </div>
    </div>
  )
}
